/**
 * `EntityHoverPopup` — Bloom-style hover surface for nodes/edges.
 *
 * Design notes:
 *  - No fetch (D4). Renders strictly from the already-loaded selection.
 *  - Controlled open via `open` / `onOpenChange` so hosts can suppress the
 *    popup while a ring menu or detail popup is active (D10).
 *  - Supports two anchor shapes:
 *      (a) `{ type: 'ref', ref }` — a DOM element ref (e.g. an event-card
 *          span) where a real `HoverCard` trigger fits. When the user hovers
 *          that element, Radix opens/closes the popup.
 *      (b) `{ type: 'virtual', ref }` — a virtual anchor whose ref is moved
 *          by the host on each frame. For non-DOM targets (Cytoscape,
 *          Cesium). In that mode we use Radix `Popover` (not `HoverCard`),
 *          controlled externally by the host.
 */

import { Popover as PopoverPrimitive } from 'radix-ui'
import { useMemo } from 'react'
import { LABEL_COLORS } from '@/components/neo4j/cytoscape/entity-icons'
import type { SelectedEdge, SelectedNode } from '@/components/neo4j/cytoscape/types'
import { filterProperties } from '@/components/neo4j/shared/PropertyItem'
import { Popover, PopoverContent } from '@/components/ui/popover'

interface LegendLabel {
  label: string
  count: number
  color: string
  borderColor?: string
}

export type HoverPopupAnchor =
  | { type: 'ref'; ref: React.RefObject<HTMLElement | null> }
  | { type: 'virtual'; ref: React.RefObject<HTMLElement | null> }

export interface EntityHoverPopupProps {
  selection: SelectedNode | SelectedEdge | null
  legendLabels?: LegendLabel[]
  /** Controls visibility. Kept external so the host can suppress it. */
  open: boolean
  onOpenChange?: (open: boolean) => void
  anchor: HoverPopupAnchor
}

function isNode(sel: SelectedNode | SelectedEdge): sel is SelectedNode {
  return 'labels' in sel && !('source' in sel)
}

function labelStyle(label: string, legend: LegendLabel[] | undefined): React.CSSProperties {
  const hit = legend?.find(l => l.label === label)
  if (hit) {
    return {
      backgroundColor: hit.color,
      color: 'var(--primary-foreground)',
      borderColor: hit.borderColor ?? hit.color,
    }
  }
  const colors = LABEL_COLORS[label] ?? LABEL_COLORS.Default
  if (!colors) {
    return { backgroundColor: 'var(--muted)', color: 'var(--foreground)' }
  }
  return { backgroundColor: colors.fill, color: '#ffffff', borderColor: colors.stroke }
}

function pickKeyProperties(
  props: Record<string, unknown> | undefined,
  max = 3
): Array<[string, unknown]> {
  if (!props) return []
  const entries = Object.entries(props).filter(
    ([key]) => filterProperties(key, false) && key !== 'is_suspect'
  )
  const priority = ['name', 'title', 'email', 'phone', 'address', 'role', 'type']
  entries.sort(([a], [b]) => {
    const ai = priority.indexOf(a.toLowerCase())
    const bi = priority.indexOf(b.toLowerCase())
    if (ai === -1 && bi === -1) return a.localeCompare(b)
    if (ai === -1) return 1
    if (bi === -1) return -1
    return ai - bi
  })
  return entries.slice(0, max)
}

function displayValue(v: unknown): string {
  if (v === null || v === undefined) return '—'
  if (typeof v === 'string') return v
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  try {
    return JSON.stringify(v)
  } catch {
    return String(v)
  }
}

function HoverBody({
  resolvedLabel,
  title,
  keyProps,
  legendLabels,
}: {
  resolvedLabel: string
  title: string
  keyProps: Array<[string, unknown]>
  legendLabels: LegendLabel[] | undefined
}) {
  return (
    <>
      <div className="flex items-center gap-2">
        <span
          className="inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
          style={labelStyle(resolvedLabel, legendLabels)}
        >
          {resolvedLabel}
        </span>
        <span className="truncate text-sm font-medium text-foreground">{title}</span>
      </div>
      {keyProps.length > 0 ? (
        <dl className="mt-2 space-y-1 text-xs">
          {keyProps.map(([k, v]) => (
            <div key={k} className="flex gap-2">
              <dt className="min-w-20 shrink-0 text-muted-foreground capitalize">
                {k.replace(/_/g, ' ')}
              </dt>
              <dd className="truncate text-foreground">{displayValue(v)}</dd>
            </div>
          ))}
        </dl>
      ) : null}
    </>
  )
}

/**
 * Standalone body for hosts that want to render the same Bloom-style summary
 * outside of a Radix Popover (e.g. as a fixed-corner panel in a graph view).
 * Takes the same `selection` / `legendLabels` props as `EntityHoverPopup`.
 */
export function EntityHoverBody({
  selection,
  legendLabels,
  className,
}: {
  selection: SelectedNode | SelectedEdge | null
  legendLabels?: LegendLabel[]
  className?: string
}) {
  const resolvedLabel = useMemo(() => {
    if (!selection) return null
    if (isNode(selection)) {
      return selection.typeLabel ?? selection.labels?.[0] ?? 'Entity'
    }
    return selection.type
  }, [selection])

  const title = useMemo(() => {
    if (!selection) return ''
    if (isNode(selection)) return selection.name || selection.id
    return `${selection.sourceName ?? selection.source} → ${selection.targetName ?? selection.target}`
  }, [selection])

  const keyProps = useMemo(() => {
    if (!selection) return []
    if (isNode(selection)) return pickKeyProperties(selection.properties)
    return pickKeyProperties(selection.properties ?? {})
  }, [selection])

  if (!selection || !resolvedLabel) return null

  return (
    <div
      className={
        className ??
        'flex w-72 flex-col gap-2.5 rounded-lg bg-popover p-2.5 text-sm text-popover-foreground shadow-md ring-1 ring-foreground/10'
      }
    >
      <HoverBody
        resolvedLabel={resolvedLabel}
        title={title}
        keyProps={keyProps}
        legendLabels={legendLabels}
      />
    </div>
  )
}

export function EntityHoverPopup({
  selection,
  legendLabels,
  open,
  onOpenChange,
  anchor,
}: EntityHoverPopupProps) {
  const resolvedLabel = useMemo(() => {
    if (!selection) return null
    if (isNode(selection)) {
      return selection.typeLabel ?? selection.labels?.[0] ?? 'Entity'
    }
    return selection.type
  }, [selection])

  const title = useMemo(() => {
    if (!selection) return ''
    if (isNode(selection)) return selection.name || selection.id
    return `${selection.sourceName ?? selection.source} → ${selection.targetName ?? selection.target}`
  }, [selection])

  const keyProps = useMemo(() => {
    if (!selection) return []
    if (isNode(selection)) return pickKeyProperties(selection.properties)
    return pickKeyProperties(selection.properties ?? {})
  }, [selection])

  if (!selection || !resolvedLabel) return null

  // Both `ref` and `virtual` anchor modes render the same controlled Popover
  // via Radix's `virtualRef` — both are positioned from a ref (a DOM element
  // on the page in `ref` mode, or an invisible host-managed anchor in
  // `virtual` mode). The popup is non-interactive (`pointer-events: none`) so
  // hovering the popup itself does not block dismissal — the host controls
  // `open` via its own hover handlers.
  return (
    <Popover open={open} onOpenChange={onOpenChange} modal={false}>
      <PopoverPrimitive.Anchor virtualRef={anchor.ref as React.RefObject<HTMLElement>} />
      <PopoverContent
        side="top"
        align="center"
        className="pointer-events-none w-72"
        onOpenAutoFocus={e => e.preventDefault()}
      >
        <HoverBody
          resolvedLabel={resolvedLabel}
          title={title}
          keyProps={keyProps}
          legendLabels={legendLabels}
        />
      </PopoverContent>
    </Popover>
  )
}
